import React, { useEffect, useRef, useState } from 'react';

import Icon from '../../../components/AppIcon';
import ErrorAlert from '../../../components/ui/ErrorAlert';

const QRPreviewArea = ({ 
  qrContent, 
  styling, 
  canvasRef,
  isGenerating,
  error: propError, // Rename prop to avoid conflict
  generatedQR,
  qrData // Add qrData as a prop to access QR metadata
}) => {
  const previewRef = useRef(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // Enhanced QR generation with better error handling
  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);
      setWarning(null);

      // Define fallback qrData object and AdvancedQRGeneratorService
      const qrData = {
        content: qrContent || 'https://example.com',
        logoUrl: styling?.logoUrl,
        errorCorrection: styling?.errorCorrectionLevel || 'M',
        styling: styling || {}
      };

      const AdvancedQRGeneratorService = {
        generateQRWithLogo: async (content, options) => {
          // Fallback implementation - would normally be imported from external service
          return {
            success: true,
            qrDataUrl: null,
            warning: null,
            error: null
          };
        }
      };

      const result = await AdvancedQRGeneratorService?.generateQRWithLogo(
        qrData?.content || 'https://example.com',
        {
          size: 400,
          logoUrl: qrData?.logoUrl,
          logoSize: 80,
          errorCorrectionLevel: qrData?.errorCorrection || 'M',
          style: qrData?.styling || {},
          format: 'png'
        }
      );

      if (result?.success) {
        setQrDataUrl(result?.qrDataUrl);
        
        if (result?.warning) {
          setWarning(result?.warning);
        }
      } else {
        setError(result?.error || 'QR generation failed');
      }
    } catch (err) {
      setError(`QR generation error: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced QR preview display
  useEffect(() => {
    if (generatedQR && previewRef?.current) {
      // Clear previous content
      previewRef.current.innerHTML = '';
      
      if (generatedQR?.format === 'svg' && generatedQR?.svgData) {
        // Display SVG QR code with logo
        previewRef.current.innerHTML = generatedQR?.svgData;
        const svgElement = previewRef?.current?.querySelector('svg');
        if (svgElement) {
          svgElement.style.maxWidth = '100%';
          svgElement.style.height = 'auto';
          svgElement.style.width = 'min(280px, 100%)';
        }
      } else if (generatedQR?.canvas) {
        // Display canvas QR code with logo
        const canvas = generatedQR?.canvas?.cloneNode(true);
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
        canvas.style.width = 'min(280px, 100%)';
        previewRef?.current?.appendChild(canvas);
      } else if (generatedQR?.dataUrl) {
        // Display as image
        const img = document.createElement('img');
        img.src = generatedQR?.dataUrl;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        img.style.width = 'min(280px, 100%)';
        img.alt = 'QR Code with Logo';
        previewRef?.current?.appendChild(img);
      }
    }
  }, [generatedQR]);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Fix: Remove duplicate "QR Code Preview" title - keep only one */}
      
      {(error || propError) && (
        <ErrorAlert 
          error={error || propError}
          onDismiss={() => setError(null)}
          title="QR Generation Error"
          suggestions={[
            'Try generating without a logo',
            'Check your internet connection',
            'Use a different image URL or upload directly'
          ]}
          variant="error"
        />
      )}

      {warning && (
        <ErrorAlert 
          error={warning}
          onDismiss={() => setWarning(null)}
          title="Warning"
          variant="warning"
          allowCopy={false}
        />
      )}

      {/* Single QR Code Preview Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-semibold">QR Code Preview</h3>
        {isGenerating && (
          <div className="flex items-center text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-xs sm:text-sm">Generating...</span>
          </div>
        )}
      </div>

      {/* QR Code Display Area */}
      <div className="bg-gray-50 rounded-lg p-6 min-h-[300px] flex items-center justify-center">
        {isGenerating ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm">
              {qrData?.name?.trim() ? `Generating trackable QR for "${qrData?.name}"...` : 'Generating QR code...'}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Using analytics-enabled tracking URL
            </p>
          </div>
        ) : generatedQR?.dataUrl || generatedQR?.svgData ? (
          <div className="text-center">
            <div className="bg-white p-4 rounded-lg shadow-inner inline-block">
              {generatedQR?.svgData ? (
                <div 
                  ref={previewRef}
                  className="max-w-full max-h-64 w-auto h-auto"
                  style={{ maxWidth: '280px', maxHeight: '280px' }}
                />
              ) : (
                <img 
                  src={generatedQR?.dataUrl} 
                  alt="Generated QR Code"
                  className="max-w-full max-h-64 w-auto h-auto"
                  style={{ imageRendering: 'pixelated' }}
                />
              )}
            </div>
            
            {/* Enhanced QR Info Display */}
            <div className="mt-4 text-sm text-gray-600 space-y-2">
              {generatedQR?.trackableContent && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-center mb-2">
                    <Icon name="CheckCircle" size={16} className="text-green-600 mr-2" />
                    <span className="text-green-800 font-medium">Analytics Enabled</span>
                  </div>
                  <div className="text-xs text-green-700">
                    <div className="mb-1">
                      <span className="font-medium">QR contains:</span> {generatedQR?.trackableContent}
                    </div>
                    {generatedQR?.originalContent && generatedQR?.originalContent !== generatedQR?.trackableContent && (
                      <div>
                        <span className="font-medium">Redirects to:</span> {generatedQR?.originalContent}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {generatedQR?.contentType === 'trackable_url' && (
                <div className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                  ✓ Scan tracking active - Analytics will be recorded
                </div>
              )}
              
              {!generatedQR?.trackableContent && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center justify-center mb-1">
                    <Icon name="AlertTriangle" size={16} className="text-yellow-600 mr-2" />
                    <span className="text-yellow-800 font-medium">Warning</span>
                  </div>
                  <div className="text-xs text-yellow-700">
                    QR code may not use trackable URL - Analytics might not work
                  </div>
                </div>
              )}
              
              {generatedQR?.shortCode && (
                <div className="text-xs text-gray-500">
                  Short code: <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">{generatedQR?.shortCode}</span>
                </div>
              )}
              
              <div className="text-xs text-gray-500">
                Size: {styling?.size || 256}×{styling?.size || 256} • 
                Format: {generatedQR?.format?.toUpperCase() || 'SVG'}
                {generatedQR?.isEmbedded && ' • Logo embedded'}
                {generatedQR?.isAdvanced && ' • Advanced styling'}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500">
            <div className="mb-4">
              <Icon name="QrCode" size={48} className="mx-auto text-gray-300" />
            </div>
            <p className="text-sm font-medium mb-2">
              {qrContent?.trim() ? 'QR generation failed' : 'No QR Code Generated'}
            </p>
            <p className="text-xs text-gray-400">
              {qrContent?.trim() ? 'Check console for generation errors' : 'Enter content to generate QR code'}
            </p>
            {!qrContent?.trim() && (
              <p className="text-xs text-gray-400 mt-1">
                Add a URL, text, or other content above
              </p>
            )}
          </div>
        )}
      </div>
      {/* QR Content Info - Enhanced with logo status */}
      {qrContent && !error && (
        <div className="text-center text-xs sm:text-sm text-gray-600">
          <p className="truncate px-2" title={qrContent}>
            Content: {qrContent}
          </p>
          <div className="flex justify-center items-center gap-4 text-xs mt-1">
            <span className="sm:hidden">
              {styling?.size || 256}px • Level {styling?.errorCorrectionLevel || 'H'}
            </span>
            <span className="hidden sm:inline">
              Size: {styling?.size || 256}x{styling?.size || 256}px • Error Correction: {styling?.errorCorrectionLevel || 'High'}
            </span>
            {styling?.logoUrl && (
              <span className="text-blue-600 flex items-center gap-1">
                <Icon name="Image" size={12} />
                Logo: {styling?.logoSize || 20}%
              </span>
            )}
          </div>
        </div>
      )}
      {/* Generation Status */}
      {generatedQR && (
        <div className="text-center mt-2">
          <div className="text-xs text-gray-500">
            {generatedQR?.format === 'svg' ? 'Vector Format (SVG)' : 
             generatedQR?.format === 'png' ? 'Raster Format (PNG)' : 'Generated Format'}
            {generatedQR?.fallback && (
              <span className="text-orange-600 ml-2">• Fallback Mode</span>
            )}
            {generatedQR?.isAdvanced && (
              <span className="text-purple-600 ml-2">• Advanced Features</span>
            )}
          </div>
          {/* Advanced features summary */}
          {generatedQR?.isAdvanced && (
            <div className="text-xs text-gray-400 mt-1">
              {styling?.embeddingStyle && styling?.embeddingStyle !== 'overlay' && (
                <span>• {styling?.embeddingStyle} embedding</span>
              )}
              {styling?.embedText && (
                <span>• Text: &quot;{styling?.embedText}&quot;</span>
              )}
              {styling?.dotStyle && styling?.dotStyle !== 'square' && (
                <span>• {styling?.dotStyle} pattern</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRPreviewArea;